// src/mobile_pages/materials/Stock.jsx
import { useEffect, useState } from "react";
import { getStockSummary, getStockHistory } from "../../api/material/extras";
import MobileSearchBar from "../../components/common/board/MobileSearchBar";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";

const __DEV__ =
  typeof process !== "undefined" && process.env?.NODE_ENV !== "production";
const DBG = (...a) => __DEV__ && console.debug("[Stock]", ...a);

const numberFmt = (v) => {
  const n = Number(v);
  if (v == null || v === "") return "-";
  return isFinite(n) ? n.toLocaleString() : "-";
};

export default function Stock() {
  const [q, setQ] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  // 재고 현황은 "품목 코드 / 품명" 기준 검색
  const [searchField, setSearchField] = useState("code");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showDebug, setShowDebug] = useState(__DEV__);

  // 선택된 품목 + 입출고 이력 팝업 상태
  const [selectedItem, setSelectedItem] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState("");

  // SearchFilterBar에서 "검색" 눌렀을 때 실행
  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const fetchData = async (reason = "manual") => {
    setLoading(true);
    setErr("");
    try {
      const projectId = localStorage.getItem("projectId");
      DBG("fetchData ->", { reason, q, page, pageSize, projectId });

      const data = await getStockSummary({
        q,
        page,
        page_size: pageSize,
        // 안전관리 문서 목록과 동일한 파라미터 구조만 맞춰둠
        search_field: searchField,
        start_date: startDate,
        end_date: endDate,
      });

      const items = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.items)
          ? data.items
          : [];

      DBG("fetchData <-", {
        count: items.length,
        total: data?.total ?? items.length,
        sample: items.slice(0, 3),
      });
      console.log(data);
      setRows(items);
      setTotal(typeof data?.total === "number" ? data.total : items.length);
    } catch (e) {
      const status = e?.response?.status;
      const url = e?.config?.url;
      const params = e?.config?.params;
      DBG("fetchData ERROR", { status, url, params, message: e?.message });
      setErr(
        `재고(자재) 목록을 불러오지 못했습니다${status ? ` (HTTP ${status})` : ""
        }.`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DBG(
      "mounted / page or search changed. projectId=",
      localStorage.getItem("projectId")
    );
    fetchData("effect:init/pageChange/search");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTrigger]);

  useEffect(() => {
    DBG("rows changed", { count: rows.length, sample: rows.slice(0, 3) });
  }, [rows]);

  // 창고 컬럼은 현재 백엔드 스키마에 없으므로 항상 false
  const hasWarehouse = false; // 현재 미사용이지만 향후 확장 대비 남겨둠

  const columns = [
    {
      key: "item_code",
      header: "품목 코드",
      // width: "300px",
      render: (row) => row.item_code || "-",
    },
    {
      key: "item_name",
      header: "품명",
      render: (row) => row.item_name || "-",
    },
    {
      key: "unit_price",
      header: "단가",
      // width: "300px",
      render: (row) => numberFmt(row.unit_price),
    },
    {
      key: "qty_on_hand",
      header: "현재가",
      // width: "300px",
      render: (row) => numberFmt(row.qty_on_hand),
    },
  ];

  // 입출고 이력 불러오기
  const openHistory = async (item) => {
    setSelectedItem(item);
    setHistoryRows([]);
    setHistoryErr("");
    setHistoryLoading(true);
    try {
      const projectId = localStorage.getItem("projectId");
      DBG("getStockHistory ->", { item, projectId });

      // 예상 응답 형식:
      // { items: [{ date, movement_type, doc_type, doc_no, location, qty_change, qty_after, note }, ...] }
      const data = await getStockHistory({
        project: projectId,
        item_id: item.item_id,
      });

      const logs = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];

      DBG("getStockHistory <-", {
        count: logs.length,
        sample: logs.slice(0, 3),
      });
      setHistoryRows(logs);
    } catch (e) {
      const status = e?.response?.status;
      const url = e?.config?.url;
      const params = e?.config?.params;
      DBG("getStockHistory ERROR", {
        status,
        url,
        params,
        message: e?.message,
      });
      setHistoryErr(
        `입출고 이력을 불러오지 못했습니다${status ? ` (HTTP ${status})` : ""}.`
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setSelectedItem(null);
    setHistoryRows([]);
    setHistoryErr("");
  };

  // 스켈레톤 로딩 컴포넌트
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-4 animate-pulse border">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
      </div>
      <div className="mt-3 flex justify-between">
        <div className="h-3 bg-gray-100 rounded w-1/3" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
    </div>
  );

  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                재고 현황
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">
                {loading
                  ? "목록을 불러오는 중…"
                  : `총 ${total.toLocaleString()}개 품목`}
              </p>
            </div>
            {__DEV__ && (
              <button
                type="button"
                onClick={() => setShowDebug((v) => !v)}
                className="text-xs px-2 py-1 rounded-lg border bg-white flex-shrink-0"
                title="디버그 패널 토글"
              >
                {showDebug ? "숨기기" : "디버그"}
              </button>
            )}
          </div>
        </div>

        {showDebug && (
          <div className="rounded-xl border bg-yellow-50 p-3 text-xs space-y-1">
            <div>
              <b>projectId</b>: {localStorage.getItem("projectId") || "(없음)"} /{" "}
              <b>q</b>: "{q}"
            </div>
            <div>
              <b>rows</b>: {rows.length} / <b>total</b>: {total} / <b>page</b>:{" "}
              {page} / <b>pageSize</b>: {pageSize}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => fetchData("debug:refresh")}
                className="px-2 py-1 rounded border bg-white"
              >
                강제 새로고침
              </button>
              <button
                onClick={() => {
                  DBG("manual console.table(rows)");
                  console.table(rows.slice(0, 20));
                }}
                className="px-2 py-1 rounded border bg-white"
              >
                콘솔 출력
              </button>
            </div>
          </div>
        )}

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {err}
          </div>
        )}

        {/* 검색 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <MobileSearchBar
            q={q}
            onChangeQ={setQ}
            onSearch={applySearch}
            searchField={searchField}
            onChangeSearchField={setSearchField}
            dateFilter={dateFilter}
            onChangeDateFilter={setDateFilter}
            searchFieldOptions={[
              { value: "code", label: "품목 코드" },
              { value: "name", label: "품명" },
            ]}
            startDate={startDate}
            endDate={endDate}
            onDateChange={({ start, end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
            pageSize={pageSize}
            onChangePageSize={setPageSize}
            placeholder="검색어를 입력해 주세요."
          />
        </div>

        {/* 재고 리스트 */}
        <div className="space-y-2">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">등록된 재고가 없습니다.</p>
            </div>
          ) : (
            rows.map((row) => (
              <button
                key={row.item_id}
                onClick={() => openHistory(row)}
                className="w-full text-left bg-white rounded-lg p-4 border hover:border-blue-300 active:bg-gray-50 transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {row.item_name || "-"}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs text-gray-500 truncate">
                        {row.item_code || "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded text-[11px]">
                    단가: {numberFmt(row.unit_price)}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-[11px]">
                    현재가: {numberFmt(row.qty_on_hand)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}

        {selectedItem && (
          <HistoryModal
            item={selectedItem}
            rows={historyRows}
            loading={historyLoading}
            error={historyErr}
            onClose={closeHistory}
          />
        )}
      </div>
    </div>
  );
}

function HistoryModal({ item, rows, loading, error, onClose }) {
  const title = `${item.item_code || "-"} / ${item.item_name || "-"}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white pt-3 pb-2 px-4 border-b z-10">
          <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">입출고 이력</div>
              <div className="text-base font-semibold truncate">{title}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg flex-shrink-0"
            >
              닫기
            </button>
          </div>
        </div>

        <div className="p-4">
          {loading && (
            <div className="mb-3 text-sm text-gray-500">
              이력을 불러오는 중입니다…
            </div>
          )}
          {error && (
            <div className="mb-3 text-sm text-red-600 border border-red-200 rounded-lg p-2 bg-red-50">
              {error}
            </div>
          )}

          <div className="space-y-3">
            {rows.length === 0 && !loading ? (
              <div className="text-center text-sm text-gray-400 py-8">
                입출고 이력이 없습니다.
              </div>
            ) : (
              rows.map((r, i) => {
                const qtyRaw =
                  r.qty_change != null ? Number(r.qty_change) : Number(r.qty);
                const isMinus = qtyRaw < 0;
                const qtyAbs = Math.abs(qtyRaw || 0);

                return (
                  <div key={r.id || i} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">일자</span>
                      <span className="text-sm font-medium">
                        {(r.date || r.created_at || "").slice(0, 10) || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">유형</span>
                      <span className="text-sm">{r.movement_type || r.type || "-"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">문서</span>
                      <span className="text-sm truncate">
                        {r.doc_type || "-"} {r.doc_no || r.dns || ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">위치/창고</span>
                      <span className="text-sm">{r.location || r.warehouse || "-"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">변동수량</span>
                      <span className="text-sm font-medium">
                        {qtyRaw == null
                          ? "-"
                          : `${isMinus ? "-" : "+"}${numberFmt(qtyAbs)}`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">변동 후 재고</span>
                      <span className="text-sm">{numberFmt(r.qty_after ?? r.balance)}</span>
                    </div>
                    {r.note || r.remark ? (
                      <div className="flex items-start justify-between">
                        <span className="text-xs text-gray-600">비고</span>
                        <span className="text-sm text-right break-words">{r.note || r.remark}</span>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-4 text-[11px] text-gray-500 text-center">
            재고 변동 내역은 입고서/출고서/조정전표 기준으로 집계됩니다.
          </div>
        </div>
      </div>
    </div>
  );
}
